import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { fileUrl } from "../lib/api";
import { Loader } from "../components/Loader";
import { StatusPill } from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { fmtDate, fmtDateTime, timeAgo } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, CalendarClock, CheckCircle2, MapPin } from "lucide-react";

function Field({ label, children }) {
  return <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p><p className="text-sm text-arq-ink mt-0.5">{children || "—"}</p></div>;
}

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sr, isLoading } = useQuery({ queryKey: ["sr", id], queryFn: async () => (await api.get(`/service-requests/${id}`)).data });

  const confirm = async () => {
    await api.post(`/service-requests/${id}/confirm`);
    qc.invalidateQueries({ queryKey: ["sr", id] });
    qc.invalidateQueries({ queryKey: ["service-requests"] });
    toast.success("Thank you! Work confirmed and request closed.");
  };

  if (isLoading || !sr) return <Loader />;

  return (
    <div className="max-w-3xl space-y-6 arq-fade-up">
      <button onClick={() => navigate("/service")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-arq-ink" data-testid="back-service"><ArrowLeft className="h-4 w-4" />Back to requests</button>

      <div className="bg-arq-card border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-sm text-arq-accent">Service Request #{sr.number}</p>
            <h1 className="font-display text-3xl text-arq-ink mt-1">{sr.category}</h1>
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-3.5 w-3.5" />{sr.location} · {sr.property?.address}</p>
          </div>
          <StatusPill status={sr.status} className="text-xs" />
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed mt-4 whitespace-pre-line">{sr.description}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
          <Field label="Submitted">{fmtDate(sr.created_at)}</Field>
          <Field label="Priority"><StatusPill status={sr.priority} /></Field>
          <Field label="Discovered">{fmtDate(sr.date_discovered)}</Field>
          <Field label="Availability">{sr.preferred_availability}</Field>
        </div>
        {sr.attachments && sr.attachments.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-4">
            {sr.attachments.map((a, i) => a.content_type?.startsWith("image") && <img key={i} src={fileUrl(a.url)} alt="" className="aspect-square object-cover rounded-md" />)}
          </div>
        )}
      </div>

      {sr.warranty_determination && (
        <div className="bg-arq-card border border-border rounded-md p-5 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-arq-green mt-0.5 shrink-0" />
          <div><p className="text-sm font-medium text-arq-ink">Warranty Determination</p><p className="text-sm text-muted-foreground mt-0.5">{sr.warranty_determination}</p></div>
        </div>
      )}

      {sr.appointment && (
        <div className="bg-arq-card border border-border rounded-md p-5 flex items-start gap-3" data-testid="appointment-card">
          <CalendarClock className="h-5 w-5 text-arq-accent mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-arq-ink">Scheduled Appointment</p>
            <p className="text-sm text-muted-foreground mt-0.5">{fmtDate(sr.appointment.date)} at {sr.appointment.start_time} ({sr.appointment.duration})</p>
            {sr.contractor && <p className="text-xs text-muted-foreground mt-1">{sr.contractor.company_name}{sr.appointment.technician ? ` · ${sr.appointment.technician}` : ""}</p>}
          </div>
        </div>
      )}

      {/* Activity & status */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-arq-card border border-border rounded-md p-5">
          <p className="font-display text-lg mb-4">Activity</p>
          {(!sr.activities || sr.activities.length === 0) ? <p className="text-sm text-muted-foreground">No updates yet.</p> : (
            <div className="space-y-4">
              {sr.activities.map((a) => (
                <div key={a.id} className="border-l-2 border-arq-surface pl-3" data-testid={`activity-${a.id}`}>
                  <p className="text-sm text-arq-ink">{a.note}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{a.by} · {timeAgo(a.at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-arq-card border border-border rounded-md p-5">
          <p className="font-display text-lg mb-4">Status History</p>
          <div className="space-y-3">
            {sr.status_history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <StatusPill status={h.status} />
                <span className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {sr.resolution && (
        <div className="bg-arq-surface/60 border border-border rounded-md p-5">
          <p className="text-sm font-medium text-arq-ink">Resolution</p>
          <p className="text-sm text-muted-foreground mt-1">{sr.resolution}</p>
        </div>
      )}

      {sr.status === "Work Completed" && !sr.homeowner_confirmed && (
        <div className="bg-arq-card border-2 border-arq-green rounded-md p-6 text-center">
          <CheckCircle2 className="h-8 w-8 text-arq-green mx-auto mb-2" strokeWidth={1.4} />
          <p className="font-medium text-arq-ink">Work has been completed</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Please confirm the work meets your satisfaction to close this request.</p>
          <Button onClick={confirm} data-testid="confirm-work">Confirm & Close Request</Button>
        </div>
      )}
    </div>
  );
}
